/**
 * PasswordTeaserCarousel
 * Web Component for password-teaser.liquid scroll-snap carousel.
 * Handles prev/next button state and scrolling.
 */
class PasswordTeaserCarousel extends HTMLElement {
  #videoObserver = null;

  connectedCallback() {
    setTimeout(() => {
      this.slider = this.querySelector('.password-teaser__carousel');
      this.prevBtn = this.querySelector('.password-teaser__prev');
      this.nextBtn = this.querySelector('.password-teaser__next');

      if (!this.slider) return;

      this.prevBtn?.addEventListener('click', this.#onPrev.bind(this));
      this.nextBtn?.addEventListener('click', this.#onNext.bind(this));
      this.slider.addEventListener('scroll', this.#onScroll.bind(this), { passive: true });

      this.#updateButtons();
      this.#initVideoObserver();
    }, 0);
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.#onPrev.bind(this));
    this.nextBtn?.removeEventListener('click', this.#onNext.bind(this));
    this.slider?.removeEventListener('scroll', this.#onScroll.bind(this));
    this.#videoObserver?.disconnect();
    this.#videoObserver = null;
  }

  #onPrev() {
    const slideWidth = this.#getSlideWidth();
    this.slider.scrollBy({ left: -slideWidth, behavior: this.#scrollBehavior() });
  }

  #onNext() {
    const slideWidth = this.#getSlideWidth();
    this.slider.scrollBy({ left: slideWidth, behavior: this.#scrollBehavior() });
  }

  #onScroll() {
    this.#updateButtons();
  }

  #getSlideWidth() {
    const firstSlide = this.slider.querySelector('.password-teaser__slide');
    if (!firstSlide) return this.slider.clientWidth;
    return firstSlide.offsetWidth + parseInt(getComputedStyle(this.slider).columnGap || '0', 10);
  }

  #updateButtons() {
    if (!this.slider) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.slider;
    const atStart = scrollLeft <= 4;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 4;

    if (this.prevBtn) {
      this.prevBtn.disabled = atStart;
      this.prevBtn.setAttribute('aria-disabled', String(atStart));
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = atEnd;
      this.nextBtn.setAttribute('aria-disabled', String(atEnd));
    }
  }

  #initVideoObserver() {
    const videos = Array.from(this.querySelectorAll('video'));
    console.log('[TeaserVideo] connectedCallback fired. videos found:', videos.length, 'slider:', this.slider?.outerHTML?.slice(0, 100));
    if (!videos.length) {
      console.log('[TeaserVideo] early return — no video elements in DOM at connectedCallback time');
      return;
    }

    this.#videoObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const video = entry.target;
        console.log('[TeaserVideo] IO callback — isIntersecting:', entry.isIntersecting, 'readyState:', video.readyState, 'paused:', video.paused, 'src:', video.currentSrc?.slice(0, 60));
        if (entry.isIntersecting) {
          const playPromise = video.play();
          console.log('[TeaserVideo] play() called, promise:', playPromise);
          playPromise?.then(() => {
            console.log('[TeaserVideo] play() resolved — video is playing');
          }).catch((err) => {
            console.log('[TeaserVideo] play() rejected:', err.name, err.message);
          });
        } else {
          video.pause();
        }
      }
    }, { threshold: 0.1 });

    for (const video of videos) {
      console.log('[TeaserVideo] observing video:', video.outerHTML?.slice(0, 150));
      this.#videoObserver.observe(video);
    }
  }

  #scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }
}

customElements.define('password-teaser-carousel', PasswordTeaserCarousel);

/**
 * PasswordTeaserModal
 * Intercepts product card link clicks in the teaser carousel and
 * opens an early-access modal instead of navigating to the product page.
 */
class PasswordTeaserModal extends HTMLElement {
  #dialog;
  #closeBtn;
  #section;
  #opener = null;
  #onCardClick;
  #onDialogClose;
  #onDialogClick;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#closeBtn = this.querySelector('.password-teaser-modal__close');
    this.#section = document.getElementById(`PasswordTeaser-${this.dataset.sectionId}`);

    this.#onCardClick = this.#handleCardClick.bind(this);
    this.#onDialogClose = this.#handleDialogClose.bind(this);
    this.#onDialogClick = this.#handleDialogClick.bind(this);

    this.#closeBtn?.addEventListener('click', () => this.close());
    this.#dialog?.addEventListener('close', this.#onDialogClose);
    this.#dialog?.addEventListener('click', this.#onDialogClick);
    this.#section?.addEventListener('click', this.#onCardClick);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener('click', this.#onCardClick);
    this.#dialog?.removeEventListener('close', this.#onDialogClose);
    this.#dialog?.removeEventListener('click', this.#onDialogClick);
  }

  #handleCardClick(event) {
    const link = event.target.closest('a');
    if (!link) return;
    const card = link.closest('.password-teaser__card--product');
    if (!card) return;
    event.preventDefault();
    this.#opener = link;
    const formId = this.dataset.klaviyoFormId;
    if (formId) {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
    } else {
      this.open();
    }
  }

  #handleDialogClick(event) {
    if (event.target === this.#dialog) this.close();
  }

  #handleDialogClose() {
    document.body.classList.remove('overflow-hidden');
    this.#opener?.focus();
    this.#opener = null;
  }

  open() {
    document.body.classList.add('overflow-hidden');
    this.#dialog?.showModal();
  }

  close() {
    this.#dialog?.close();
  }
}

customElements.define('password-teaser-modal', PasswordTeaserModal);
